/**
 * Enumeration of available permission tier names in the system.
 *
 * @enum {string} PermissionName
 * @description Defines the hierarchical permission-tier structure. A user's
 * access to a given module is determined by which tier their role grants for
 * that module. Tiers are ordered from lowest to highest privilege.
 *
 * @example
 * ```typescript
 * // Check user permission tier
 * if (userPermission === PermissionName.ADMIN) {
 *   // User has admin-tier permission for this module
 * }
 *
 * // Tier hierarchy check
 * const canManage = userPermission === PermissionName.ADMIN ||
 *                   userPermission === PermissionName.SUPER_VISOR;
 * ```
 *
 * @since 1.0.0
 * @author SADIN Development Team
 */
export enum PermissionName {
    /**
     * Guest tier - unauthenticated / unprivileged placeholder
     * @type {string}
     * @description Used as a sentinel for users with no assigned tier on a module.
     * Treated as no-access by the permission-tier guards and directives.
     */
    GUEST = 'Guest',

    /**
     * Viewer tier - read-only access
     * @type {string}
     * @description Can view data but cannot modify anything
     */
    VIEWER = 'Viewer',

    /**
     * Operator tier - basic operational access
     * @type {string}
     * @description Can perform basic operations and control certain functions
     */
    OPERATOR = 'Operator',

    /**
     * Supervisor tier - management access
     * @type {string}
     * @description Can create, update, and manage data within assigned modules
     */
    SUPER_VISOR = 'Supervisor',

    /**
     * Admin tier - administrative access
     * @type {string}
     * @description Can delete data and has full administrative privileges
     */
    ADMIN = 'Admin',

    /**
     * Facility Owner tier - highest access tier
     * @type {string}
     * @description Highest privilege tier with complete facility control
     */
    OWNER = 'FACILITIEY-OWNER',
}

/**
 * Union type for permission tier name strings.
 *
 * @type {string}
 * @description String literal union type for permission names.
 * Provides type safety when working with permission names as strings.
 *
 * @example
 * ```typescript
 * function checkPermission(name: PermissionNameType): boolean {
 *   return name === 'Admin' || name === 'Supervisor';
 * }
 * ```
 */
export type PermissionNameType =
    | 'Admin'
    | 'Supervisor'
    | 'Operator'
    | 'Viewer'
    | 'Guest'
    | 'FACILITIEY-OWNER';

/**
 * Combined type for permission tier names.
 *
 * @type {PermissionName | PermissionNameType}
 * @description Union type that accepts both enum values and string literals.
 * Provides maximum flexibility when working with permission names.
 *
 * @example
 * ```typescript
 * function processPermission(name: IPermissionNameType): void {
 *   // Can accept both enum and string values
 *   console.log(`Processing permission: ${name}`);
 * }
 * ```
 */
export type IPermissionNameType = PermissionName | PermissionNameType;
